import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ArrowLeft, RefreshCw, Play, Square, Coins, TrendingUp, Pause } from 'lucide-react'
import { request, requestMobile } from '@/lib/request'
import { esQuery } from '@/lib/es'
import { toast } from 'sonner'
import TaskTrendChart from '@/components/charts/TaskTrendChart'

interface TaskInfo {
  id: string
  name: string
  account: string
  balance: number
  max: number
  status: string
  mode: string
  window_status: string
  use_count: number
  current_count: number
  plat: string
  strategy: string
  update_time: string
  node?: string
}

interface FlowItem {
  account: string
  balance: number
  cur: string
  bit: number
  current_pay: number
  code: string
  live_id: string
  pay_count: number
  current_count: number
  strategy: string
  date: string
  timestamp: number
}

interface IpPoolItem {
  account: string
  address: string
  content: string
  duration: string
  bindHost: string
  transform_url: string
  is_rd: boolean
  update_time: string
  create_time: string
}

export default function TaskDetail() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [taskInfo, setTaskInfo] = useState<TaskInfo | null>(null)
  const [flowData, setFlowData] = useState<FlowItem[]>([])
  const [ipPoolData, setIpPoolData] = useState<IpPoolItem[]>([])
  const [taskLog, setTaskLog] = useState<string>('')
  const [loadingFlow, setLoadingFlow] = useState(false)
  const [loadingIp, setLoadingIp] = useState(false)
  const [loadingLog, setLoadingLog] = useState(false)
  const [logLines, setLogLines] = useState('100')
  const logEndRef = useRef<HTMLDivElement>(null)

  // Flow data time range
  const [timeRange, setTimeRange] = useState('1h')

  // IP Pool pagination
  const [ipPageSize, setIpPageSize] = useState(10)
  const [ipPageNum, setIpPageNum] = useState(1)
  const [ipTotal, setIpTotal] = useState(0)

  const loadTaskInfo = async () => {
    try {
      const resp = await request('/system/api/query/task', {
        page_size: 1,
        page_num: 1,
        query_filter: { id },
      })
      if (resp && resp.code === 0 && resp.data.list.length) {
        setTaskInfo(resp.data.list[0])
      }
    } catch (error) {
      toast.error('加载任务信息失败')
    }
  }

  const loadFlowData = async () => {
    if (!taskInfo?.account) return

    setLoadingFlow(true)
    try {
      const body = {
        query: {
          bool: {
            filter: [
              {
                term: {
                  'account.keyword': taskInfo.account,
                },
              },
            ],
            must: [
              {
                range: {
                  timestamp: {
                    gte: `now-${timeRange}`,
                    lte: 'now',
                  },
                },
              },
            ],
          },
        },
        sort: [{ timestamp: { order: 'desc' } }],
      }

      const result = await esQuery('xr_pay_new', body)
      if (result) {
        const rs = result.data ? result.data : result
        const list = rs.hits.hits.map((val: any) => val._source)
        setFlowData(list)
      }
    } catch (error) {
      console.error('加载流程数据失败:', error)
      toast.error('加载流程数据失败')
    } finally {
      setLoadingFlow(false)
    }
  }

  const loadIpPoolData = async () => {
    if (!taskInfo?.account) return

    setLoadingIp(true)
    try {
      const resp = await request('/system/api/query/ip_pool', {
        page_size: ipPageSize,
        page_num: ipPageNum,
        query_filter: { account: taskInfo.account },
      })
      if (resp && resp.code === 0) {
        setIpPoolData(resp.data.list || [])
        setIpTotal(resp.data.total || 0)
      }
    } catch (error) {
      toast.error('加载IP池数据失败')
    } finally {
      setLoadingIp(false)
    }
  }

  const loadTaskLog = async () => {
    if (!taskInfo?.name) return

    setLoadingLog(true)
    try {
      const requestBody: any = {
        t_name: taskInfo.name,
      }

      // 添加行数参数（如果用户输入了有效值）
      if (logLines && logLines.trim()) {
        requestBody.n = logLines.trim()
      }

      const resp = await requestMobile('/common/apis/v1/task_log', requestBody)
      if (resp && resp.code === 0) {
        setTaskLog(resp.data || '')
      }
    } catch (error) {
      console.error('加载任务日志失败:', error)
      toast.error('加载任务日志失败')
    } finally {
      setLoadingLog(false)
    }
  }

  // Task operation handlers
  const handleRestartTask = async () => {
    if (!taskInfo?.name || !taskInfo?.node) return

    try {
      const resp = await requestMobile('/common/apis/v1/restart_task', {
        task_name: taskInfo.name,
        server_name: taskInfo.node,
      })
      if (resp && resp.code === 0) {
        toast.success('任务重启成功')
        loadTaskInfo()
      }
    } catch (error) {
      toast.error('任务重启失败')
    }
  }

  const handleStopTask = async () => {
    if (!taskInfo?.name || !taskInfo?.node) return

    try {
      const resp = await requestMobile('/common/apis/v1/stop_task', {
        task_name: taskInfo.name,
        server_name: taskInfo.node,
      })
      if (resp && resp.code === 0) {
        toast.success('任务停止成功')
        loadTaskInfo()
      }
    } catch (error) {
      toast.error('任务停止失败')
    }
  }

  const handleWithdrawMode = async () => {
    if (!taskInfo?.name) return

    try {
      const resp = await requestMobile('/common/apis/v1/set_wd', {
        t_name: taskInfo.name,
        plat: taskInfo.plat,
      })
      if (resp && resp.code === 0) {
        toast.success('已切换到提现模式')
        loadTaskInfo()
      }
    } catch (error) {
      toast.error('切换提现模式失败')
    }
  }

  const handleRunningMode = async () => {
    if (!taskInfo?.name) return

    try {
      const resp = await requestMobile('/common/apis/v1/set_run_s', {
        t_name: taskInfo.name,
      })
      if (resp && resp.code === 0) {
        toast.success('已切换到跑分模式')
        loadTaskInfo()
      }
    } catch (error) {
      toast.error('切换跑分模式失败')
    }
  }

  const handlePauseTask = async () => {
    if (!taskInfo?.id) return

    try {
      const resp = await request('/system/api/update/task', {
        id: taskInfo.id,
        current_count: -1
      })
      if (resp && resp.code === 0) {
        toast.success('任务已暂停')
        loadTaskInfo()
      } else {
        toast.error(resp?.msg || '任务暂停失败')
      }
    } catch (error) {
      console.error('Failed to pause task:', error)
      toast.error('任务暂停失败')
    }
  }

  useEffect(() => {
    if (id) {
      loadTaskInfo()
    }
  }, [id])

  useEffect(() => {
    if (taskInfo) {
      loadFlowData()
      loadIpPoolData()
      loadTaskLog()
    }
  }, [taskInfo, timeRange, ipPageSize, ipPageNum])

  // 自动滚动到日志底部
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [taskLog])

  if (!taskInfo) {
    return (
      <div className="h-full p-6 flex items-center justify-center">
        <div className="text-muted-foreground">加载中...</div>
      </div>
    )
  }

  return (
    <div className="h-full p-6 overflow-auto">
      <div className="space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/task')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{taskInfo.name}</h1>
              <p className="text-sm text-muted-foreground">任务详情</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleRestartTask}>
              <Play className="h-4 w-4 mr-1" />
              重启任务
            </Button>
            <Button variant="outline" size="sm" onClick={handleStopTask}>
              <Square className="h-4 w-4 mr-1" />
              停止任务
            </Button>
            <Button variant="outline" size="sm" onClick={handlePauseTask}>
              <Pause className="h-4 w-4 mr-1" />
              暂停任务
            </Button>
            <Button variant="outline" size="sm" onClick={handleWithdrawMode}>
              <Coins className="h-4 w-4 mr-1" />
              提现模式
            </Button>
            <Button variant="outline" size="sm" onClick={handleRunningMode}>
              <TrendingUp className="h-4 w-4 mr-1" />
              跑分模式
            </Button>
            <Button variant="outline" onClick={() => {
              loadFlowData()
              loadIpPoolData()
              loadTaskLog()
            }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              刷新数据
            </Button>
          </div>
        </div>

        {/* Task Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>基本信息</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">账号</div>
                <div className="font-medium">{taskInfo.account}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">余额</div>
                <div className="font-medium text-emerald-600 dark:text-emerald-400">
                  {taskInfo.balance?.toFixed(2) || '0.00'}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">限额</div>
                <div className="font-medium text-orange-600 dark:text-orange-400">
                  {taskInfo.max || '-'}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">运行状态</div>
                <div>
                  <Badge variant={taskInfo.status === 'running' ? 'default' : 'secondary'}>
                    {taskInfo.status}
                  </Badge>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">运行模式</div>
                <div>
                  <Badge
                    className={
                      taskInfo.mode === 'run'
                        ? 'bg-blue-500 hover:bg-blue-600'
                        : 'bg-purple-500 hover:bg-purple-600'
                    }
                  >
                    {taskInfo.mode === 'run' ? '▶ RUN' : '🔬 R&D'}
                  </Badge>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">平台</div>
                <div className="font-medium">{taskInfo.plat || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">已运行</div>
                <div className="font-medium">{taskInfo.use_count || 0}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">本次运行</div>
                <div className="font-medium">{taskInfo.current_count || 0}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Task Trend Chart */}
        <TaskTrendChart
          taskName={taskInfo.account}
          refreshFlag={loadingFlow}
          showTimeSelector={true}
        />

        {/* Flow Data Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>流程数据</CardTitle>
                <CardDescription>查看最近的流程记录</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  className="w-24"
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  placeholder="1h"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadFlowData}
                  disabled={loadingFlow}
                >
                  <RefreshCw className={`h-4 w-4 ${loadingFlow ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>余额</TableHead>
                    <TableHead>货币</TableHead>
                    <TableHead>下注</TableHead>
                    <TableHead>当前支付</TableHead>
                    <TableHead>代码</TableHead>
                    <TableHead>直播ID</TableHead>
                    <TableHead>支付次数</TableHead>
                    <TableHead>当前次数</TableHead>
                    <TableHead>策略</TableHead>
                    <TableHead>时间</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flowData.length > 0 ? (
                    flowData.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.balance}</TableCell>
                        <TableCell>{item.cur}</TableCell>
                        <TableCell>{item.bit}</TableCell>
                        <TableCell>{item.current_pay}</TableCell>
                        <TableCell>{item.code}</TableCell>
                        <TableCell>{item.live_id}</TableCell>
                        <TableCell>{item.pay_count}</TableCell>
                        <TableCell>{item.current_count}</TableCell>
                        <TableCell>{item.strategy}</TableCell>
                        <TableCell className="text-xs">{item.date}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                        {loadingFlow ? '加载中...' : '暂无数据'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="text-sm text-muted-foreground mt-2">共 {flowData.length} 条记录</div>
          </CardContent>
        </Card>

        {/* IP Pool Data Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>IP 池数据</CardTitle>
                <CardDescription>该任务关联的 IP 池记录</CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadIpPoolData}
                disabled={loadingIp}
              >
                <RefreshCw className={`h-4 w-4 ${loadingIp ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>地址</TableHead>
                    <TableHead>内容</TableHead>
                    <TableHead>时长</TableHead>
                    <TableHead>绑定主机</TableHead>
                    <TableHead>转换URL</TableHead>
                    <TableHead>R&D</TableHead>
                    <TableHead>更新时间</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ipPoolData.length > 0 ? (
                    ipPoolData.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-xs">{item.address}</TableCell>
                        <TableCell>{item.content}</TableCell>
                        <TableCell>{item.duration}</TableCell>
                        <TableCell>{item.bindHost}</TableCell>
                        <TableCell className="max-w-xs truncate">{item.transform_url}</TableCell>
                        <TableCell>
                          {item.is_rd ? (
                            <Badge variant="secondary">rd</Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{item.update_time}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        {loadingIp ? '加载中...' : '暂无数据'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* IP Pool Pagination */}
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">共 {ipTotal} 条记录</div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={ipPageNum === 1}
                  onClick={() => setIpPageNum((p) => Math.max(1, p - 1))}
                >
                  上一页
                </Button>
                <span className="text-sm">
                  第 {ipPageNum} 页 / 共 {Math.ceil(ipTotal / ipPageSize)} 页
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={ipPageNum >= Math.ceil(ipTotal / ipPageSize)}
                  onClick={() => setIpPageNum((p) => p + 1)}
                >
                  下一页
                </Button>
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={ipPageSize}
                  onChange={(e) => {
                    setIpPageSize(Number(e.target.value))
                    setIpPageNum(1)
                  }}
                >
                  <option value={10}>10 条/页</option>
                  <option value={20}>20 条/页</option>
                  <option value={50}>50 条/页</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Task Log Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>任务日志</CardTitle>
                <CardDescription>查看任务运行日志</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">行数:</span>
                  <Input
                    className="w-24"
                    value={logLines}
                    onChange={(e) => setLogLines(e.target.value)}
                    placeholder="100"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadTaskLog}
                  disabled={loadingLog}
                >
                  <RefreshCw className={`h-4 w-4 ${loadingLog ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-96 border rounded-lg bg-muted/20">
              <div className="p-4 font-mono text-xs whitespace-pre-wrap">
                {taskLog ? (
                  <>
                    {taskLog}
                    <div ref={logEndRef} />
                  </>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    {loadingLog ? '加载中...' : '暂无日志'}
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
